import { useTick } from '@/hooks/useTick'

interface ServiceOutageHeaderProps {
  title: string
  /** The person on the hook for this vendor; omitted where nobody is named. */
  maintainer?: string
  /** When the current outage began; the counter runs from here. */
  downSince: Date
  /**
   * A second line under the counter — the vendor's indicator code and the status
   * it went to.
   *
   * This is where an estimated-loss figure used to sit. The feed reports when a
   * service went down and what it went down *to*, but nothing about what the
   * downtime cost, and a number that specific ("38 Designs Unsynced") is not one
   * to invent — so the row carries what is actually known.
   */
  note?: string
}

/** Elapsed time since `from`, as "01:02:26". Hours are not wrapped at 24. */
function elapsed(from: Date, now: number): string {
  const total = Math.max(0, Math.floor((now - from.getTime()) / 1000))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor(total / 60) % 60)}:${pad(total % 60)}`
}

/**
 * The head of a system screen while it is down: who owns it on the left, how
 * long it has been down on the right.
 *
 * The counter ticks live rather than showing a stamp, because the number people
 * act on is the elapsed time, not the moment it started. Mono and tabular so the
 * digits don't reflow every second.
 *
 * Rendered only when the feed reports an open incident — a page that always
 * showed an outage banner would be one nobody reads.
 */
export function ServiceOutageHeader({
  title,
  maintainer,
  downSince,
  note,
}: ServiceOutageHeaderProps) {
  useTick(1000)
  const downFor = elapsed(downSince, Date.now())

  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        {/* 32px on desktop, matching every other system page's heading. */}
        <h1 className="text-fg text-3xl leading-tight font-semibold tracking-tight sm:text-[32px]">
          {title}
        </h1>
        {maintainer && <p className="text-fg-muted mt-1 text-sm">Maintained by {maintainer}</p>}
      </div>

      <div className="shrink-0 text-right">
        <p className="flex flex-wrap items-baseline justify-end gap-x-3 font-mono">
          <span className="text-critical text-xl tracking-tight sm:text-2xl">
            Has been down for
          </span>
          <span
            className="text-critical text-2xl font-bold tabular-nums sm:text-3xl"
            aria-label={`Down for ${downFor}`}
          >
            {downFor}
          </span>
        </p>
        {note && (
          <p className="text-degraded mt-1 text-right font-mono text-sm">{note}</p>
        )}
      </div>
    </div>
  )
}
